import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetShowReminderSettings, apiPutShowReminderSettings, apiSendShowReminderNow } from '../api';
import type { ReminderMember } from '../api';
import { useWriteGate } from '../lib/writeGate';
import { venueLocalTimeFor, formatServerHour } from '../lib/reminderTimeDisplay';
import { withCardIcon } from '../lib/uiIcons';

/**
 * ShowReminderSettings — the daysheet-reminder card for a single show, shown in
 * the show's Day Sheet (Day tab). Moved here from Band Settings on 2026-08-23:
 * each show now has its own on/off flag, send hour, lead time (day offset) and
 * recipients.
 *
 * Since 2026-10-11 (LOT J) the lead time, send hour AND recipient list are ALWAYS
 * visible — even while the switch is OFF — so they can be prepared/checked
 * ahead of time; the PUT is independent of `enabled`, so saving works in that
 * state. A "Send now" button sends the daysheet immediately (editor+), without
 * touching reminder_log, so the scheduled send is never suppressed.
 *
 * The recipient picker is limited to the members ACTIVE on THIS show (the
 * show's rider active musicians + active crew — not the whole band) who have
 * an email. The server returns them via `available_members` on the GET, using
 * the same getShowActiveMemberIds() source of truth the send job uses, so the
 * checkbox list always matches who can actually be emailed.
 *
 * Reads for any viewer+ on the show's band; writes/sends are disabled for
 * viewers (editor+ enforced server-side too).
 */
export default function ShowReminderSettings({
  showId,
  canWrite,
  bandId,
  venueTz,
  showDate,
}: {
  showId: string | null;
  canWrite: boolean;
  /** The show's band — feeds the capability-aware write gate (lot A2). */
  bandId?: string | null;
  /** The venue's IANA timezone (show.timezone || country-derived), if known. */
  venueTz?: string | null;
  /** The show's date (YYYY-MM-DD...): the send date is that minus day_offset. */
  showDate?: string | null;
}) {
  const { t } = useTranslation();
  // Capability-aware write gate (lot A2). `PUT /api/shows/:id/reminder-settings`
  // is mounted AFTER the server's global read-only guard (server/index.js:624)
  // → 403 for a `users.role = 'read-only'` account. The `canWrite` prop comes
  // from ShowDetailPage's own gate on `form.band_id`; re-deriving it here on the
  // SHOW's band (not on whatever band happens to be active) keeps the two in
  // agreement and lets this card be reused on its own.
  const writeGate = useWriteGate(bandId);
  const writable = canWrite && writeGate.canWrite;
  const [enabled, setEnabled] = useState(false);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [hour, setHour] = useState(6);
  const [dayOffset, setDayOffset] = useState(1);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [members, setMembers] = useState<ReminderMember[]>([]);

  useEffect(() => {
    if (!showId) return;
    let cancelled = false;
    apiGetShowReminderSettings(showId)
      .then((r) => {
        if (cancelled) return;
        setEnabled(Number(r.enabled) === 1);
        setRecipients(Array.isArray(r.recipients) ? r.recipients : []);
        setHour(Number.isFinite(Number(r.reminder_hour)) ? Number(r.reminder_hour) : 6);
        setDayOffset(Number.isFinite(Number(r.day_offset)) ? Number(r.day_offset) : 1);
        setMembers(Array.isArray(r.available_members) ? r.available_members : []);
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setLoaded(true); }); // viewer/403 → keep defaults
    return () => { cancelled = true; };
  }, [showId]);

  const save = async () => {
    if (!writable) return; // frozen for a global read-only account (server 403s)
    if (!showId || saving) return;
    setSaving(true);
    setSaved(false);
    setSendMsg(null);
    try {
      const r = await apiPutShowReminderSettings(showId, {
        enabled: enabled ? 1 : 0,
        recipients,
        reminder_hour: hour,
        day_offset: dayOffset,
      });
      setEnabled(Number(r.enabled) === 1);
      setRecipients(Array.isArray(r.recipients) ? r.recipients : []);
      setHour(Number.isFinite(Number(r.reminder_hour)) ? Number(r.reminder_hour) : 6);
      setDayOffset(Number.isFinite(Number(r.day_offset)) ? Number(r.day_offset) : 1);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // save failed — keep local state so the user can retry
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (!writable || !showId || sending) return;
    if (members.length === 0) return; // no one with an email on this show
    setSending(true);
    setSendMsg(null);
    try {
      const r = await apiSendShowReminderNow(showId);
      const n = Array.isArray(r.recipients) ? r.recipients.length : 0;
      setSendMsg({ ok: true, text: t('reminders.sendSentTo', { count: n }) });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setSendMsg({ ok: false, text: t('reminders.sendFailed', { msg }) });
    } finally {
      setSending(false);
    }
  };

  const toggleRecipient = (id: string) => {
    setRecipients((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // Lead-time options: 0 = on the show day, 1..7 = that many days before.
  const leadLabel = (n: number) =>
    n === 0 ? t('reminders.leadOnShowDay') : n === 1 ? t('reminders.leadOneDayBefore') : t('reminders.leadNDaysBefore', { n });

  // The day the email actually goes out = show date minus the lead time. Its
  // value picks the right DST offset for the venue (never a fixed +1/+2).
  const sendDate = (() => {
    const s = showDate ? String(showDate).slice(0, 10) : '';
    const base = s ? new Date(`${s}T12:00:00Z`) : new Date();
    if (Number.isNaN(base.getTime())) return undefined;
    const d = new Date(base.getTime());
    d.setUTCDate(d.getUTCDate() - dayOffset);
    return d;
  })();
  const venueLocal = venueLocalTimeFor(hour, venueTz, sendDate);

  return (
    <details className="card" data-daytab="reminders" style={{ maxWidth: 1000, marginBottom: 20 }}>
      <summary className="rc-card-head">
        <div>
          <h3 className="rc-header-title">{withCardIcon('show-day:reminders', t('reminders.title'))}</h3>
          <p className="rc-header-sub">{t('reminders.hint')}</p>
        </div>
      </summary>
      <div style={{ marginTop: 12 }}>
      <div className="rc-form-row" data-reminder-toggle>
        <label className="toggle-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: writable ? 'pointer' : 'not-allowed' }}>
          <input
            type="checkbox"
            checked={enabled}
            disabled={!writable}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span>{t(enabled ? 'reminders.enabled' : 'reminders.disabled')}</span>
        </label>
      </div>

      {/* Always visible (even OFF): lead time + send hour + recipients. The PUT
          does not depend on `enabled`, so they can be prepared/saved now and
          apply as soon as the switch is turned on. */}
      <div style={{ marginBottom: 16 }}>
        <p className="text-dim" style={{ color: 'var(--text-dim)', marginBottom: 8 }}>
          {t('reminders.scheduleHint')}
        </p>
        <div className="rc-form-row" data-reminder-schedule>
          <label htmlFor="reminder-lead" style={{ marginRight: 6 }}>{t('reminders.leadLabel')}</label>
          <select
            id="reminder-lead"
            className="form-input"
            style={{ flex: '0 0 auto', width: 200 }}
            value={dayOffset}
            disabled={!writable}
            onChange={(e) => {
              const v = Number(e.target.value);
              setDayOffset(Number.isFinite(v) ? Math.max(0, Math.min(7, v)) : 1);
            }}
          >
            {Array.from({ length: 8 }, (_, n) => n).map((n) => (
              <option key={n} value={n}>{leadLabel(n)}</option>
            ))}
          </select>
          <label htmlFor="reminder-hour" style={{ marginLeft: 12, marginRight: 6 }}>{t('reminders.sendHourHint')}</label>
          <select
            id="reminder-hour"
            className="form-input"
            style={{ flex: '0 0 auto', width: 140 }}
            value={hour}
            disabled={!writable}
            onChange={(e) => setHour(Math.max(0, Math.min(23, Number(e.target.value) || 0)))}
          >
            {Array.from({ length: 24 }, (_, h) => h).map((h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, '0')}:00
              </option>
            ))}
          </select>
        </div>
        {/* Server hour vs venue local hour, so the reader is never off by a
            timezone. When the venue timezone is unknown we show the server hour
            alone plus a discreet tip (no error, no blocking). */}
        <div data-reminder-time-equivalent style={{ marginTop: 8 }}>
          <p className="text-dim" style={{ color: 'var(--text-dim)', marginBottom: 4 }}>
            {venueLocal
              ? t('reminders.timeEquivalent', {
                  server: formatServerHour(hour),
                  venue: venueLocal.hhmm,
                  tz: venueLocal.tz,
                })
              : t('reminders.serverTimeOnly', { server: formatServerHour(hour) })}
          </p>
          <p className="text-dim" style={{ color: 'var(--text-dim)' }}>
            {venueLocal ? t('reminders.serverSendsAt') : t('reminders.venueTzTip')}
          </p>
        </div>
        <p className="text-dim" style={{ color: 'var(--text-dim)', marginTop: 6 }}>
          {t('reminders.keptWhenOff')}
        </p>

        <p className="text-dim" style={{ color: 'var(--text-dim)', marginBottom: 8, marginTop: 12 }}>
          {t('reminders.recipientsHint')}
        </p>
        {members.length === 0 ? (
          <p className="text-dim" style={{ color: 'var(--text-dim)' }}>{t('reminders.noMembers')}</p>
        ) : (
          <div className="rc-list" data-reminder-recipients>
            {members.map((m) => (
              <label
                key={m.id}
                className="rc-card"
                data-reminder-recipient
                style={{ cursor: writable ? 'pointer' : 'not-allowed' }}
              >
                <input
                  type="checkbox"
                  disabled={!writable}
                  checked={recipients.includes(m.id)}
                  onChange={() => toggleRecipient(m.id)}
                />
                <span className="rc-card-body">
                  <span className="rc-card-name">{m.nickname || m.name}</span>
                  <span className="rc-card-meta"><span>{m.email}</span></span>
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {writable && (
        <div className="rc-card-fields">
          <button
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={!loaded || saving}
          >
            {saving ? t('reminders.saving') : t('reminders.save')}
          </button>
          <button
            className="btn"
            data-reminder-send-now
            onClick={() => void sendNow()}
            disabled={!loaded || sending || saving || members.length === 0}
            title={members.length === 0 ? t('reminders.sendNoRecipients') : undefined}
            style={{ marginLeft: 8 }}
          >
            {sending ? t('reminders.sending') : t('reminders.sendNow')}
          </button>
          {saved && <span style={{ color: 'var(--success)' }}>{t('reminders.saved')}</span>}
          {sendMsg && (
            <span
              data-reminder-send-result
              style={{ marginLeft: 12, color: sendMsg.ok ? 'var(--success)' : 'var(--danger, #e94560)' }}
            >
              {sendMsg.text}
            </span>
          )}
        </div>
      )}
      </div>
    </details>
  );
}
